import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface DeleteCompanyResponse {
  message: string;
  deletedUsers: number;
  companyName: string;
}

export function useCompanyDeletion() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async ({ companyId, confirmationName, cancelSubscriptions }: { companyId: string; confirmationName: string; cancelSubscriptions?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('delete-company', {
        body: {
          companyId,
          confirmationName,
          cancelSubscriptions,
        },
      });

      if (error) {
        throw error;
      }

      return data as DeleteCompanyResponse;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['company-users'] });
      
      toast({
        title: "Empresa excluída com sucesso",
        description: `A empresa "${data.companyName}" foi excluída junto com ${data.deletedUsers} usuário(s).`,
      });
    },
    onError: (error: any) => {
      console.error('Company deletion error:', error);
      
      let errorMessage = 'Erro ao excluir empresa';
      
      // Check for active subscriptions error
      if (error?.body?.requiresCancellation === true || 
          error?.message?.toLowerCase().includes('active subscription') ||
          error?.details?.toLowerCase().includes('active subscription')) {
        errorMessage = 'Esta empresa possui assinaturas ativas. Marque "Cancelar assinaturas ativas" no modal e tente novamente.';
      }
      // Tratamento específico para função não encontrada
      else if (error?.status === 404 || error?.message?.includes('404') || error?.message?.includes('not found')) {
        errorMessage = 'Função de exclusão não está disponível. Entre em contato com o suporte.';
      } else if (error?.status === 401 || error?.status === 403) {
        errorMessage = 'Você não tem permissão para excluir empresas.';
      } else if (error?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente em alguns minutos.';
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.details) {
        errorMessage = error.details;
      } else if (error?.error_description) {
        errorMessage = error.error_description;
      } else if (error?.body?.error) {
        errorMessage = error.body.error;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Log detalhado para debug
      console.error('Detailed error info:', {
        error,
        message: error?.message,
        details: error?.details,
        status: error?.status,
        code: error?.code,
      });

      toast({
        title: "Erro na exclusão",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  return {
    deleteCompany: (companyId: string, confirmationName: string, cancelSubscriptions?: boolean) => 
      deleteMutation.mutateAsync({ companyId, confirmationName, cancelSubscriptions }),
    isDeleting: deleteMutation.isPending,
    error: deleteMutation.error,
  };
}